import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuthStore } from '@/stores/auth-store'
import type { Tables, TablesInsert, TablesUpdate } from '@/types/supabase'

type ShopProduct = Tables<'shop_products'>

export function useShopProducts() {
  const tenantId = useAuthStore((s) => s.currentTenant?.id)

  return useQuery({
    queryKey: ['shop_products', tenantId],
    enabled: !!tenantId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('shop_products')
        .select('*')
        .eq('tenant_id', tenantId!)
        .order('display_order', { ascending: true })

      if (error) throw error
      return data as ShopProduct[]
    },
  })
}

export function useCreateProduct() {
  const qc = useQueryClient()
  const tenantId = useAuthStore((s) => s.currentTenant?.id)

  return useMutation({
    mutationFn: async (input: Omit<TablesInsert<'shop_products'>, 'tenant_id'>) => {
      const { data, error } = await supabase
        .from('shop_products')
        .insert({ ...input, tenant_id: tenantId! })
        .select()
        .single()

      if (error) throw error
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['shop_products', tenantId] }),
  })
}

export function useUpdateProduct() {
  const qc = useQueryClient()
  const tenantId = useAuthStore((s) => s.currentTenant?.id)

  return useMutation({
    mutationFn: async ({ id, ...update }: TablesUpdate<'shop_products'> & { id: string }) => {
      const { data, error } = await supabase
        .from('shop_products')
        .update(update)
        .eq('id', id)
        .select()
        .single()

      if (error) throw error
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['shop_products', tenantId] }),
  })
}

export function useDeleteProduct() {
  const qc = useQueryClient()
  const tenantId = useAuthStore((s) => s.currentTenant?.id)

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('shop_products').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['shop_products', tenantId] }),
  })
}
